import { Component, HostBinding, OnDestroy, ViewEncapsulation } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { fromEvent, Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { ICellRendererParams, GridReadyEvent, ViewportChangedEvent } from '@ag-grid-community/core';
import { AgGridModule } from '@ag-grid-community/angular';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { NgIf, NgFor, DatePipe } from '@angular/common';

import { pageTransitions } from '../../../../shared/animations/page-transitions';
import { UserService } from '../../../../shared/services/user.service';
import { User } from '../../../../shared/models/user';
import {
	LANGUAGE_OPTIONS,
	LANGUAGE_OPTIONS_KEYS,
	DOWNLOAD_OPTIONS_KEYS,
	DOWNLOAD_OPTIONS,
} from '../../../../shared/models/list';
import { AGGREGATED } from '../../../../shared/models/settings';
import { TranslationService } from '../../../../shared/services/translation.service';
import { PersonMember, UserInfo } from '../../../../shared/models/user-info';
import { ConnectionService } from '../../../../shared/services/connection.service';
import { CommonGridOptions, clientSideAgGridModules } from '../../../../shared/datacenter/grid-options';
import { CommonColDef, LinkColDef } from '../../../../shared/datacenter/column-defs';
import { UNDERSCORE } from '../../../../shared/utils/constant';
import { UserBasics } from '../../../../shared/models/user-basics';
import { UserCardService } from '../../../services/user-card.service';
import { LocationService } from '../../../../shared/services/location.service';
import { LoadingSpinnerComponent } from '../../../../shared/components/utils/loading-spinner.component';

interface Options<T> {
	value: T;
	viewValue: string;
}

interface GroupOptions<T> {
	disabled?: boolean;
	name: T;
	vgd: Options<number>[];
}

interface UserStatusColParams {
	data: PersonMember;
}

type Lang = 'en' | 'es' | 'fr' | 'de' | 'it' | 'pl';
type Download = 'aggregated' | 'duplicate';

const resizeObservable = fromEvent(globalThis, 'resize');

const linkToOrganizationCardCellRendererParams = {
	routerLink(params: ICellRendererParams<PersonMember, unknown>) {
		const organization = params.data;
		return organization?.member
			? {
				commands: [
					'/user',
					'organization',
					organization.member.id,
					'details'
				]
			}
			: undefined;
	}
};

@Component({
	selector: 'copo-user-self-card-settings',
	templateUrl: './user-self-card-settings.component.html',
	animations: pageTransitions,
	styleUrl: './user-self-card-settings.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		FormsModule,
		ReactiveFormsModule,
		NgIf,

		MatButtonModule,
		TranslateModule,
		MatIconModule,

		MatInputModule,

		MatFormFieldModule,

		MatSelectModule,
		NgFor,

		MatOptionModule,
		AgGridModule,
		LoadingSpinnerComponent,
		DatePipe
	]
})
export class UserCardSettingsComponent implements OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;

	readonly UNDERSCORE = UNDERSCORE;

	emailPattern = '^[a-zA-Z0-9._\'-]+@[a-zA-Z0-9.-]{2,}[.][a-zA-Z]{2,}$';
	user: User | undefined;
	userInfo?: UserInfo;
	userClone?: UserInfo;
	gridOptions?: CommonGridOptions<UserInfo>;
	editForm = false;
	userBasics?: UserBasics;
	enableEdit? = false;
	userForm!: FormGroup;
	visibilityGroupControl = new FormControl();
	visibilityGroupOptions: GroupOptions<string>[] = [];
	editUser: UserBasics | undefined;
	languageControl = new FormControl();
	langOptions: Options<Lang>[] = [];
	initialLang: Lang = 'en';

	downloadControl = new FormControl();
	downloadOptions: Options<Download>[] = [];
	initialDownload: Download = AGGREGATED;

	userId?: number;
	resizeSub?: Subscription;
	readonly languageOptions = LANGUAGE_OPTIONS;
	readonly modules = clientSideAgGridModules;
	private userServiceSub: Subscription = Subscription.EMPTY;

	constructor(
		private readonly userService: UserService,
		private readonly translationService: TranslationService,
		private readonly connectionService: ConnectionService,

		private readonly snackBar: MatSnackBar,
		private readonly route: ActivatedRoute,
		private readonly router: Router,
		private readonly userCardService: UserCardService,
		private readonly locationService: LocationService,
	) {
		if (!this.route.parent?.parent) {
			throw new Error('wrong url');
		}
		const loginUser = this.userService.loggedUser;
		if (loginUser === undefined) {
			return;
		}

		const res = <{ user: UserInfo }> this.route.parent.parent.snapshot.data;
		this.userId = res['user'].id;
		const userInfo = this.userInfo = res['user'];
		let canManageUsers = false;
		for (const pm of userInfo.personMembers) {
			canManageUsers = loginUser.canUpdateOrganizationUsers(pm.member ? pm.member.id : 0);
			if (canManageUsers) {
				break;
			}
		}

		this.enableEdit = this.isSelfCard() || canManageUsers;
		this.initAgGrid(this.userInfo.personMembers);
		this.user = this.userService.user;
		for (const key of DOWNLOAD_OPTIONS_KEYS) {
			this.downloadOptions.push({ value: key, viewValue: DOWNLOAD_OPTIONS[key] });
		}
		const downloadSetting = this.user.getSettings('download');
		this.downloadControl.setValue(downloadSetting?.value.type || this.initialDownload);
		for (const vg of this.user.visibilities) {
			if (vg.organizationRole.toLowerCase() !== 'buyer') {
				continue;
			}
			const group = this.visibilityGroupOptions.find(value => vg.supplierName === value.name);
			const customers = group ? group.vgd : [];
			customers.push({ value: vg.id, viewValue: vg.customName || vg.organizationName });
			if (!group) {
				this.visibilityGroupOptions.push({ name: vg.supplierName, vgd: customers });
			}
		}
		this.visibilityGroupControl.setValue(this.user.currentVisibility.id);
		for (const key of LANGUAGE_OPTIONS_KEYS) {
			this.langOptions.push({ value: key, viewValue: LANGUAGE_OPTIONS[key] });
			if (key !== this.translationService.currentLanguage) {
				continue;
			}
			this.initialLang = key;
		}
		this.languageControl.setValue(this.translationService.currentLanguage || this.initialLang);

		this.userForm = new FormGroup({
			firstName: new FormControl('', Validators.required),
			lastName: new FormControl('', Validators.required),
			email: new FormControl('', [Validators.required, Validators.pattern(this.emailPattern)]),
			phoneNumber: new FormControl('', [Validators.pattern(String.raw`^((\+91-?)|0)?[0-9]{10}$`)])
		});
	}

	initAgGrid(personMember: PersonMember[]) {
		this.gridOptions = new CommonGridOptions<UserInfo>({
			translationService: this.translationService,
			multiSortKey: 'ctrl',
			domLayout: 'autoHeight',
			rowData: personMember,
			columnDefs: [
				this.userId
					? new CommonColDef<PersonMember>({
						headerName: this.translationService.getTR('settings.organizations.name'),
						valueGetter: params =>
							this.formatColumnValue(params.data),
						sort: 'asc'
					})
					: new LinkColDef<PersonMember>({
						headerName: this.translationService.getTR('settings.organizations.name'),
						valueGetter: params =>
							this.formatColumnValue(params.data),
						cellRendererParams: linkToOrganizationCardCellRendererParams,
						sort: 'asc'
					}),
				new CommonColDef<PersonMember>({
					headerName: this.translationService.getTR('settings.organizations.role'),
					colId: 'role',
					field: 'role',
					width: 100,
					valueGetter: params => {
						const organization = params.data;
						return organization?.role
							? this.translationService.getTR(`organizationrole.${ organization.role }`)
							: '';
					}
				}),
				new CommonColDef<PersonMember>({
					headerName: this.translationService.getTR('eoffer.status'),
					field: 'status',
					minWidth: 80,
					maxWidth: 400,
					width: 100,
					valueGetter: params => {
						if (!params.data || !this.userInfo) {
							return '';
						}
						const personMemberStatus = params.data.status;
						const personStatus = this.userInfo.status;

						if (personMemberStatus.toLowerCase() === 'pend') {
							return this.translationService.getTR('issue.pending');
						}
						return personStatus.toLowerCase() === 'pend'
							? this.translationService.getTR('wait.user.validation')
							: this.translationService.getTR('active');
					},
					cellStyle: params =>
						this.getFontColorByStatus(params.data),
					sortable: true
				}),
			]
		});
	}

	async onClickReject(params: UserStatusColParams) {
		const personMemberResponse = <PersonMember | undefined>params.data;
		if (personMemberResponse?.member) {
			const userIdToReject = this.userId ? Number(this.userId) : this.user?.id;
			if (userIdToReject) {
				await this.rejectUser(userIdToReject, personMemberResponse.member.id);
				if (this.userInfo?.personMembers.length === 1) {
					this.locationService.go(-1, `/user/organization/${ personMemberResponse.member.id }/users`);
				}
			}
		}
	}

	async onClickAccept(params: UserStatusColParams) {
		const personMemberResponse = <PersonMember | undefined>params.data;
		if (personMemberResponse?.member) {
			const userIdToAccept = this.userId ? Number(this.userId) : this.user?.id;
			if (userIdToAccept) {
				await this.acceptUser(userIdToAccept, personMemberResponse.member.id);
			}
		}
	}

	async reload() {
		const currentUrl = this.router.url;
		await this.router.navigate(['redirect'], { queryParams: { path: currentUrl }, replaceUrl: true });
	}

	async rejectUser(userId: number, orgId: number) {
		try {
			await this.userService.rejectPersonMemberRole(userId, orgId);
			await this.reload();
		} catch (err) {
			console.error('Error from http put current user detail', err);
		}
	}

	async acceptUser(userId: number, orgId: number) {
		try {
			await this.userService.acceptPersonMemberRole(userId, orgId);
			await this.reload();
		} catch (err) {
			console.error('Error from http put current user detail', err);
		}
	}

	getAcceptIcon(status: string) {
		if (status === 'pend') {
			return {
				iconName: 'md:done',
				className: 'mat-done',
			};
		}

		return '';
	}

	getRejectIcon(status: string) {
		if (status === 'pend') {
			return {
				iconName: 'close',
				className: 'mat-accent',
			};
		}

		return '';
	}

	isSelfCard() {
		const loginUser = this.userService.loggedUser;
		if (loginUser === undefined) {
			return false;
		}
		return this.router.url.includes('self') || this.userId === loginUser.id;
	}

	async handleDownloadUpdated(value: Download) {
		try {
			if (!this.user) {
				return;
			}
			const downloadSetting = this.user.getSettings('download');
			if (!downloadSetting) {
				this.user.settings.push({
					key: 'download',
					value: {
						version: 1,
						type: value
					}
				});
			} else {
				downloadSetting.value = {
					version: 1,
					type: value
				};
			}
			await this.userService.updateUserSettings(this.user);
			await this.userService.updateUser(this.user);
			this.snackBar.open(
				this.translationService.getTR(
					'settings.snackbar.downloadType',
					{
						downloadType: this.translationService.getTR(DOWNLOAD_OPTIONS[value])
					}
				),
				undefined,
				{
					duration: 2000,
				}
			);
		} catch (err) {
			console.error(err);
			this.displayErrorMessage();
		}
	}

	async handleLanguageUpdated(value: Lang) {
		try {
			if (!this.user) {
				return;
			}
			this.user.language = value;
			this.translationService.setCurrentLanguage(value);
			await this.userService.updateLanguage(value);
			await this.userService.updateUserData();
			if (this.userInfo) {
				this.userInfo.language = value;
			}
			this.snackBar.open(
				this.translationService.getTR(
					'settings.snackbar.language',
					{
						lang: LANGUAGE_OPTIONS[value]
					}
				),
				undefined,
				{
					duration: 2000,
				}
			);
		} catch (err) {
			console.error(err);
			if (!this.user) {
				return;
			}
			this.user.language = this.initialLang;
			this.translationService.setCurrentLanguage(this.initialLang);
			this.displayErrorMessage();
		}
	}

	async handleVisibilityUpdate(value: number) {
		if (!this.user) {
			return;
		}
		if (value === this.user.currentVisibility.id) {
			return;
		}
		await this.userService.updateUserVisibility(value);
		const selectedVisibility = this.user.visibilities.find(visi => visi.id === value);
		if (!selectedVisibility) {
			return;
		}
		this.snackBar.open(
			this.translationService.getTR(
				'settings.snackbar.visibility',
				{
					visibility: selectedVisibility.customName || selectedVisibility.organizationName,
					supplier: selectedVisibility.supplierName,
				}
			),
			undefined,
			{
				duration: 2000,
			}
		);
	}

	formatColumnValue(personMember: PersonMember | undefined) {
		if (personMember === undefined) {
			return '';
		}
		return personMember.member
			? personMember.member.name
			: '';
	}

	ngOnDestroy() {
		this.userServiceSub.unsubscribe();
		if (this.resizeSub) {
			this.resizeSub.unsubscribe();
		}
	}

	displayErrorMessage() {
		this.snackBar.open(this.translationService.getTR('snackbar.offline'), undefined, {
			duration: 2000,
		});
	}

	async changeDelegatedOrganization() {
		if (!this.user) {
			return;
		}
		await this.connectionService.requestLogout(false, { userId: this.user.id });
	}

	onGridReady(params: GridReadyEvent) {
		params.api.sizeColumnsToFit();
		const resizeSub = resizeObservable.subscribe(() => {
			params.api.sizeColumnsToFit();
		});
		if (!this.resizeSub) {
			this.resizeSub = resizeSub;
		} else {
			this.resizeSub.add(resizeSub);
		}
	}

	onViewportChanged(params: ViewportChangedEvent) {
		params.api.sizeColumnsToFit();
	}

	onEdit() {
		this.editUser = this.userBasics;
		this.editForm = true;
		if (this.userInfo) {
			this.userClone = {
				...this.userInfo,
				address: {
					...this.userInfo.address
				}
			};
		}
		this.updateUserForm(this.userInfo);
	}

	onCancel() {
		this.editForm = false;
		this.userInfo = this.userClone;
	}

	updateUserForm(user?: UserInfo) {
		if (!user) {
			return;
		}
		this.userForm.patchValue({
			firstName: user.firstName,
			lastName: user.lastName,
			email: user.address.email,
			phoneNumber: user.address.phoneNumber,
			language: user.language
		});
	}

	getUserData() {
		const fields = [
			'id',
			'firstName',
			'lastName',
			'language',
			'username',
			'address.email',
			'address.phoneNumber',
			'address.phoneNumber2',
			'address.requestedMail',
			'personMembers.member',
			'personMembers.role',
			'personMembers.status',
			'lastMember',
			'lastConnectionDate',
			'creationDate',
			'timeFormat',
			'dateFormat',
			'status',
			'userType'
		];
		if (!this.user) {
			return;
		}
		const userIdToUpdate = this.userId ? Number(this.userId) : this.user.id;
		this.userServiceSub = this.userService.getUserDetailsById(
			String(userIdToUpdate),
			fields
		).subscribe({
			next: userInfo => {
				this.userInfo = userInfo;
			}
		});
	}

	updateUserInfo() {
		if (!this.userInfo) {
			return;
		}
		this.userInfo.firstName = <string> this.userForm.get('firstName')?.value;
		this.userInfo.lastName = <string> this.userForm.get('lastName')?.value;
		this.userInfo.address.email = <string> this.userForm.get('email')?.value;
		this.userInfo.address.phoneNumber = <string> this.userForm.get('phoneNumber')?.value;
	}

	async onSave() {
		this.updateUserInfo();
		if (this.userInfo === undefined) {
			return;
		}
		if (!this.user) {
			return;
		}
		const isEmailUpdateRequested = this.userClone && this.userInfo.address.email
			!== this.userClone.address.email;
		this.userBasics = {
			firstname: this.userClone && this.userInfo.firstName
			!== this.userClone.firstName ? this.userInfo.firstName : undefined,
			lastname: this.userClone && this.userInfo.lastName !== this.userClone.lastName
				? this.userInfo.lastName : undefined,
			email: isEmailUpdateRequested ? this.userInfo.address.email : undefined,
			phoneNumber: this.userClone && this.userInfo.address.phoneNumber !== this.userClone.address.phoneNumber
				? this.userInfo.address.phoneNumber : undefined,
		};
		const userIdToUpdate = this.userId ? Number(this.userId) : this.user.id;
		try {
			await this.userService.userToUpdate(userIdToUpdate, this.userBasics).then(
				() => {
					if (this.userBasics) {
						if (this.userBasics.firstname) {
							this.userCardService.setFirstName(this.userBasics.firstname);
						}
						if (this.userBasics.lastname) {
							this.userCardService.setLastName(this.userBasics.lastname);
						}
						if (isEmailUpdateRequested) {
							this.snackBar.open(this.translationService.getTR('email-message'), undefined, {
								duration: 5000
							});
						}
					}
					this.getUserData();
				}
			);
		} catch (err) {
			console.error('Error from http put current user detail', err);
			if (err instanceof HttpErrorResponse) {
				this.snackBar.open((<{ code: string }>err.error).code, undefined, {
					duration: 5000
				});
			}
			this.userInfo = this.userClone;
		}
		this.editForm = false;
		this.userClone = undefined;
	}

	private getFontColorByStatus(personMember?: PersonMember): { color?: string } {
		if (!personMember) {
			return { color: 'red' };
		}
		const status = personMember.status === 'pend'
			? 'userValidationPending' : this.userInfo?.status === 'pend' ? 'pend' : 'activ';
		switch (status) {
		case 'activ':
			return { color: 'green' };
		case 'pend':
			return { color: 'grey' };
		case 'userValidationPending':
			return { color: 'orange' };
		default:
			return { color: 'red' };
		}
	}
}
